/**
 * Google Sign-In composable using Google Identity Services (GIS)
 *
 * Uses the same client ID as Android to ensure server token verification works.
 * The server verifies tokens using GoogleIdTokenVerifier with this audience.
 *
 * This implementation uses the FedCM/One Tap flow with a fallback to the
 * rendered Google button approach for better reliability.
 */

import { ref, onScopeDispose } from 'vue'

// Google Client ID - same as Android and server audience
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string

// Debug mode - only logs in development
const DEBUG = import.meta.env.DEV

function log(...args: unknown[]) {
  if (DEBUG) {
    console.log('[GoogleAuth]', ...args)
  }
}

// Type definitions for Google Identity Services
interface CredentialResponse {
  credential: string // The ID token (JWT)
  select_by: string
  clientId?: string
}

interface GsiButtonConfiguration {
  type?: 'standard' | 'icon'
  theme?: 'outline' | 'filled_blue' | 'filled_black'
  size?: 'large' | 'medium' | 'small'
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin'
  shape?: 'rectangular' | 'pill' | 'circle' | 'square'
  logo_alignment?: 'left' | 'center'
  width?: number
  locale?: string
  click_listener?: () => void
}

interface IdConfiguration {
  client_id: string
  callback: (response: CredentialResponse) => void
  auto_select?: boolean
  cancel_on_tap_outside?: boolean
  context?: 'signin' | 'signup' | 'use'
  itp_support?: boolean
  use_fedcm_for_prompt?: boolean
}

interface PromptMomentNotification {
  isDisplayMoment: () => boolean
  isDisplayed: () => boolean
  isNotDisplayed: () => boolean
  getNotDisplayedReason: () => string
  isSkippedMoment: () => boolean
  getSkippedReason: () => string
  isDismissedMoment: () => boolean
  getDismissedReason: () => string
}

interface Google {
  accounts: {
    id: {
      initialize: (config: IdConfiguration) => void
      prompt: (callback?: (notification: PromptMomentNotification) => void) => void
      renderButton: (parent: HTMLElement, config: GsiButtonConfiguration) => void
      disableAutoSelect: () => void
      cancel: () => void
      revoke: (hint: string, callback: () => void) => void
    }
  }
}

declare global {
  interface Window {
    google?: Google
  }
}

export function useGoogleAuth() {
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const isInitialized = ref(false)

  let credentialCallback: ((idToken: string) => void) | null = null
  let buttonContainerId: string | null = null

  // Google Identity Services renders an iframe and its own DOM into the button
  // container, and attaches mutation observers Vue knows nothing about. If we
  // let Vue unmount that subtree (e.g. router.push after signup navigates away
  // from the page hosting the button), Vue walks a DOM that GIS has mutated and
  // throws "Cannot read properties of null (reading 'nextSibling')", which then
  // cascades into "Cannot destructure property 'bum' of null" as the unmount
  // aborts mid-way. Cancelling One Tap and emptying the container before Vue
  // tears it down means Vue only ever removes a clean, empty element.
  function cleanup() {
    try {
      window.google?.accounts?.id?.cancel()
    } catch {
      // GIS may already be gone; nothing to cancel.
    }

    if (buttonContainerId) {
      const element = document.getElementById(buttonContainerId)
      element?.replaceChildren()
    }
  }

  // Runs as part of the host component's scope teardown, before Vue removes the
  // component's DOM subtree.
  onScopeDispose(cleanup)

  /**
   * Initialize Google Identity Services
   * Must be called after the GIS script has loaded
   */
  function initialize(onCredential: (idToken: string) => void) {
    log('Initializing Google Auth...')
    log('Client ID:', GOOGLE_CLIENT_ID)
    log('Current origin:', window.location.origin)

    credentialCallback = onCredential

    if (!window.google?.accounts?.id) {
      log('Google script not loaded yet, waiting...')

      // Script not loaded yet, wait for it
      const checkInterval = setInterval(() => {
        if (window.google?.accounts?.id) {
          log('Google script loaded!')
          clearInterval(checkInterval)
          doInitialize()
        }
      }, 100)

      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkInterval)
        if (!isInitialized.value) {
          log('ERROR: Google script failed to load after 10s')
          error.value = 'Google Sign-In failed to load. Please refresh the page.'
        }
      }, 10000)
    } else {
      doInitialize()
    }
  }

  function doInitialize() {
    if (!window.google?.accounts?.id) {
      log('ERROR: window.google.accounts.id is undefined')
      return
    }

    log('Calling google.accounts.id.initialize()')

    try {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
        context: 'signin',
        itp_support: true,
        use_fedcm_for_prompt: true,
      })

      isInitialized.value = true
      log('Google Auth initialized successfully')

      // If a button container was registered, render the button now
      if (buttonContainerId) {
        renderButtonInternal(buttonContainerId)
      }
    } catch (e) {
      log('ERROR initializing Google Auth:', e)
      error.value = 'Failed to initialize Google Sign-In'
    }
  }

  function handleCredentialResponse(response: CredentialResponse) {
    log('Received credential response:', {
      hasCredential: !!response.credential,
      selectBy: response.select_by,
      credentialLength: response.credential?.length,
    })

    isLoading.value = false

    if (response.credential) {
      log('Calling credential callback with token')
      credentialCallback?.(response.credential)
    } else {
      log('ERROR: No credential in response')
      error.value = 'No credential received from Google'
    }
  }

  /**
   * Trigger the Google Sign-In using One Tap prompt
   * Note: This may not work in all browsers due to third-party cookie restrictions
   */
  function signInWithOneTap() {
    if (!window.google?.accounts?.id) {
      log('ERROR: Google not initialized for One Tap')
      error.value = 'Google Sign-In not initialized'
      return
    }

    error.value = null
    isLoading.value = true
    log('Triggering One Tap prompt...')

    window.google.accounts.id.prompt((notification) => {
      log('One Tap notification:', {
        isDisplayMoment: notification.isDisplayMoment(),
        isDisplayed: notification.isDisplayed(),
        isNotDisplayed: notification.isNotDisplayed(),
        isSkippedMoment: notification.isSkippedMoment(),
        isDismissedMoment: notification.isDismissedMoment(),
      })

      if (notification.isNotDisplayed()) {
        const reason = notification.getNotDisplayedReason()
        log('One Tap not displayed, reason:', reason)

        isLoading.value = false

        // Map reasons to user-friendly messages
        const reasonMessages: Record<string, string> = {
          browser_not_supported:
            'Your browser does not support Google Sign-In. Please try a different browser.',
          invalid_client: 'Configuration error. Please contact support.',
          missing_client_id: 'Configuration error. Please contact support.',
          opt_out_or_no_session: 'Please sign in to Google in another tab first, then try again.',
          secure_http_required: 'Google Sign-In requires HTTPS.',
          suppressed_by_user:
            'Google Sign-In was previously closed. Click the button to try again.',
          unregistered_origin: `This domain (${window.location.origin}) is not registered for Google Sign-In. Please add it to GCP Console.`,
          unknown_reason: 'Unable to show Google Sign-In. Please try again.',
        }

        error.value = reasonMessages[reason] || `Google Sign-In unavailable: ${reason}`
      } else if (notification.isSkippedMoment()) {
        const reason = notification.getSkippedReason()
        log('One Tap skipped, reason:', reason)
        isLoading.value = false
      } else if (notification.isDismissedMoment()) {
        const reason = notification.getDismissedReason()
        log('One Tap dismissed, reason:', reason)
        isLoading.value = false

        if (reason === 'credential_returned') {
          log('Credential was returned (success)')
        }
      }
    })
  }

  /**
   * Register a container element for the Google Sign-In button
   * The button will be rendered when Google is initialized
   */
  function registerButtonContainer(elementId: string) {
    log('Registering button container:', elementId)
    buttonContainerId = elementId

    if (isInitialized.value) {
      renderButtonInternal(elementId)
    }
  }

  function renderButtonInternal(elementId: string) {
    if (!window.google?.accounts?.id) {
      log('ERROR: Cannot render button, Google not initialized')
      return
    }

    const element = document.getElementById(elementId)
    if (!element) {
      log('ERROR: Button container element not found:', elementId)
      return
    }

    log('Rendering Google button in:', elementId)

    try {
      window.google.accounts.id.renderButton(element, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        width: element.offsetWidth || 300,
        click_listener: () => {
          log('Google button clicked')
          isLoading.value = true
          error.value = null
        },
      })
      log('Google button rendered successfully')
    } catch (e) {
      log('ERROR rendering Google button:', e)
    }
  }

  /**
   * Main sign-in function - uses the rendered button approach
   * which is more reliable than One Tap
   */
  function signIn() {
    log('signIn() called')

    // If we have a rendered button, click it programmatically
    if (buttonContainerId) {
      const container = document.getElementById(buttonContainerId)
      const button = container?.querySelector('div[role="button"]') as HTMLElement
      if (button) {
        log('Clicking rendered Google button')
        button.click()
        return
      }
    }

    // Fall back to One Tap
    log('No rendered button found, falling back to One Tap')
    signInWithOneTap()
  }

  return {
    isLoading,
    error,
    isInitialized,
    initialize,
    signIn,
    signInWithOneTap,
    registerButtonContainer,
    GOOGLE_CLIENT_ID,
  }
}
